import { useState } from "react";
import useSWR from "swr";
import { Box, Flex, Grid, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import baseService from "../../services/base";
import Loader from "../../components/Feedback/Loader";
import Narrative from "./Narrative";
import KpiCard from "./KpiCard";
import FilterBar, { filtersToQuery } from "./FilterBar";

// EG-38 — Dashboard "Portfólio macro" (consultoria). Reusa GET /metrics/portfolio.

const INK = "#292B30";
const INK_MUTED = "#5C6066";
const BRAND = "#0073BB";
const GOOD = "#2F855A";
const WARN = "#C05621";
const DANGER = "#C53030";
const SURFACE = "#FFFFFF";

const fetcher = (url) => baseService.get(url).then((r) => r?.data);

function StatusTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box bg={SURFACE} border="1px solid #E2E8F0" borderRadius="8px" p="10px" boxShadow="sm">
      <Text fontSize="13px" fontWeight={600} color={INK} textTransform="capitalize">
        {label}
      </Text>
      <Text fontSize="12px" color={INK_MUTED}>
        {payload[0].value} certificações
      </Text>
    </Box>
  );
}

export default function PortfolioDashboard() {
  const [filters, setFilters] = useState({});
  const { data, isLoading } = useSWR(
    `metrics/portfolio${filtersToQuery(filters)}`,
    fetcher
  );

  return (
    <Box>
      <FilterBar value={filters} onChange={setFilters} />
      {isLoading ? (
        <Loader />
      ) : !data ? (
        <Flex bg={SURFACE} borderRadius="12px" p="48px" justify="center">
          <Text color={INK_MUTED}>Não foi possível carregar o portfólio.</Text>
        </Flex>
      ) : (
        <PortfolioContent data={data} />
      )}
    </Box>
  );
}

function PortfolioContent({ data }) {
  const rate = Math.round((data.certificates?.certificationRate ?? 0) * 100);
  const statusRows = (data.byStatus || []).map((s) => ({
    status: s.status,
    count: s.count,
  }));

  const expiring = data.validity?.expiringSoon ?? 0;
  const narrative = `${data.companies?.total ?? 0} empresas no portfólio, ${
    data.companies?.active ?? 0
  } com questionário ativo. Taxa de certificação em ${rate}%.${
    expiring ? ` Atenção: ${expiring} certificado(s) vencem nos próximos 90 dias.` : ""
  }`;

  return (
    <Box>
      <Narrative tone={expiring ? "warn" : "info"}>{narrative}</Narrative>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="16px" mb="24px">
        <KpiCard
          label="Empresas"
          value={data.companies?.total ?? 0}
          hint={`${data.companies?.active ?? 0} com questionário ativo`}
        />
        <KpiCard
          label="Certificações em processo"
          value={data.certificates?.total ?? 0}
          hint={`${data.certificates?.certified ?? 0} auditadas`}
        />
        <KpiCard label="Taxa de certificação" value={`${rate}%`} color={BRAND} />
        <KpiCard
          label="A vencer (90 dias)"
          value={data.validity?.expiringSoon ?? 0}
          hint={`${data.validity?.expired ?? 0} vencidas`}
          color={data.validity?.expiringSoon ? WARN : INK}
        />
      </SimpleGrid>

      <Grid templateColumns={{ base: "1fr", xl: "1.4fr 1fr" }} gap="24px">
        {/* Distribuição por etapa do fluxo */}
        <Box bg={SURFACE} borderRadius="12px" p={["20px", "28px"]}>
          <Heading size="sm" color={INK} mb="16px">
            Certificações por etapa
          </Heading>
          {statusRows.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statusRows} margin={{ top: 16, right: 8, bottom: 8, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
                <XAxis
                  dataKey="status"
                  tick={{ fontSize: 11, fill: INK_MUTED }}
                  tickLine={false}
                  axisLine={{ stroke: "#E2E8F0" }}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: INK_MUTED }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<StatusTooltip />} cursor={{ fill: "rgba(0,115,187,0.06)" }} />
                <Bar dataKey="count" fill={BRAND} radius={[4, 4, 0, 0]} maxBarSize={56}>
                  <LabelList dataKey="count" position="top" fontSize={12} fill={INK_MUTED} />
                  {statusRows.map((r) => (
                    <Cell key={r.status} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Text color={INK_MUTED} fontSize="14px">
              Sem certificações em processo ainda.
            </Text>
          )}
        </Box>

        {/* Validade das certificações emitidas */}
        <Box bg={SURFACE} borderRadius="12px" p={["20px", "28px"]}>
          <Heading size="sm" color={INK} mb="16px">
            Validade dos certificados
          </Heading>
          <Flex direction="column" gap="12px">
            <ValidityRow label="Ativos (+90 dias)" value={data.validity?.active ?? 0} color={GOOD} />
            <ValidityRow
              label="A vencer (90 dias)"
              value={data.validity?.expiringSoon ?? 0}
              color={WARN}
            />
            <ValidityRow label="Vencidos" value={data.validity?.expired ?? 0} color={DANGER} />
          </Flex>
        </Box>
      </Grid>

      {/* Detalhe: qual empresa e qual certificado tem cada vencimento. */}
      {(data.validityDetail?.length ?? 0) > 0 && (
        <Box bg={SURFACE} borderRadius="12px" p={["20px", "28px"]} mt="24px">
          <Heading size="sm" color={INK} mb="4px">
            Vencimentos por empresa
          </Heading>
          <Text fontSize="12px" color={INK_MUTED} mb="16px">
            Ordenado do mais próximo de vencer (ou já vencido) para o mais distante.
          </Text>
          <Box maxH="360px" overflowY="auto">
            {data.validityDetail.map((v, i) => {
              const c = v.bucket === "expired" ? DANGER : v.bucket === "expiring" ? WARN : GOOD;
              const dateStr = new Date(v.validUntil).toLocaleDateString();
              const dias =
                v.days < 0 ? `vencido há ${Math.abs(v.days)} dia(s)` : `vence em ${v.days} dia(s)`;
              return (
                <Flex
                  key={i}
                  align="center"
                  justify="space-between"
                  gap="12px"
                  p="12px 14px"
                  borderTop={i === 0 ? "none" : "1px solid #EDF2F7"}
                  flexWrap="wrap"
                >
                  <Box minW="200px" flex="1">
                    <Text fontSize="14px" fontWeight={600} color={INK}>
                      {v.company}
                    </Text>
                    <Text fontSize="12px" color={INK_MUTED}>
                      {v.certificate}
                    </Text>
                  </Box>
                  <Flex align="center" gap="12px">
                    <Text fontSize="13px" color={INK_MUTED} whiteSpace="nowrap">
                      {dateStr}
                    </Text>
                    <Text
                      fontSize="12px"
                      fontWeight={600}
                      color={c}
                      bg={`${c}14`}
                      px="10px"
                      py="3px"
                      borderRadius="full"
                      whiteSpace="nowrap"
                    >
                      {dias}
                    </Text>
                  </Flex>
                </Flex>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ValidityRow({ label, value, color }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      bg="#F8F8FB"
      borderRadius="10px"
      p="14px 16px"
      borderLeft={`4px solid ${color}`}
    >
      <Text fontSize="14px" color={INK}>
        {label}
      </Text>
      <Text fontSize="20px" fontWeight={700} color={color}>
        {value}
      </Text>
    </Flex>
  );
}
